import { CollectionButton, CollectionButtonProps } from '@buggy/shared'
import { Tooltip } from 'antd'
import { TooltipPropsWithTitle } from 'antd/es/tooltip'
import {
  FunctionComponent,
  ReactNode,
  RefObject,
  useRef,
  useState,
} from 'react'
import styled, { useTheme } from 'styled-components'
import AddKolsToCollectionModal, {
  AddKolsToCollectionModalProps,
} from '@/components/common/modals/add-kols-to-collection-modal'
import { createTourStepData } from '@/components/tour/utils'
import { useCollectionActionPermission } from '@/hooks/use-permission'
import { useIntl } from '@/i18n/hooks/use-intl'
import { Nullable } from '@/types/nullable'
import { AddToProjectProperties } from '@/utils/ampli'

interface AddToCollectionButtonProps
  extends Pick<AddKolsToCollectionModalProps, 'from' | 'platform'>,
    Pick<CollectionButtonProps, 'type'> {
  kol: AddKolsToCollectionModalProps['kols'][number]
  tooltip?: TooltipPropsWithTitle
  onCollectedChange?: AddKolsToCollectionModalProps['onCollectedChange']
  containerRef?: 'parent' | Nullable<RefObject<HTMLDivElement>>
  isDemo?: boolean
  customButton?: ReactNode
  isClickedFromDrawer?: AddToProjectProperties['isClickedFromDrawer']
  isDisabled?: boolean
  /**
   *  @description Should stop propagation on click
   *  @default true
   */
  shouldStopPropagationOnClick?: boolean
}

const AddToCollectionButton: FunctionComponent<AddToCollectionButtonProps> = ({
  kol,
  from,
  platform,
  type,
  containerRef = 'parent',
  tooltip: propTooltip,
  onCollectedChange,
  isDemo,
  isClickedFromDrawer,
  customButton,
  isDisabled = false,
  shouldStopPropagationOnClick = true,
}) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const collectionPermission = useCollectionActionPermission()
  const triggerElementRef = useRef<HTMLDivElement>(null)

  const [addKolsToCollectionModalOpen, setAddKolsToCollectionModalOpen] =
    useState(false)
  const disabled = isDisabled || !collectionPermission || isDemo
  const tooltip = collectionPermission
    ? propTooltip
    : {
        title: formatMessage({
          id: 'workspace:insufficient_permission_tooltip',
        }),
      }

  return (
    <AddToCollectionButtonContainer
      ref={triggerElementRef}
      onClick={(event): void => {
        // 避免點擊 modal 中收藏庫連結被擋掉
        if (
          event.target instanceof HTMLElement &&
          event.target.closest('[data-collection-link]')
        ) {
          return
        }
        event.preventDefault()

        if (shouldStopPropagationOnClick) {
          event.stopPropagation()
        }
      }}
    >
      <Tooltip {...tooltip}>
        <TriggerElement
          {...createTourStepData('search-kol', 4)}
          {...createTourStepData('social-performance', 3)}
          onClick={(): void => {
            if (disabled) {
              return
            }
            setAddKolsToCollectionModalOpen(true)
          }}
        >
          {customButton ?? (
            <CollectionButton
              activeColor={theme.colors.brand.primary}
              collected={kol.isCollectionKol}
              disabled={disabled}
              type={type}
            />
          )}
        </TriggerElement>
      </Tooltip>
      <AddKolsToCollectionModal
        containerRef={
          containerRef === 'parent'
            ? triggerElementRef
            : (containerRef ?? undefined)
        }
        from={from}
        isClickedFromDrawer={isClickedFromDrawer}
        kols={[kol]}
        open={addKolsToCollectionModalOpen}
        platform={platform}
        onClose={(): void => {
          setAddKolsToCollectionModalOpen(false)
        }}
        onCollectedChange={(changedKolId, isCollected): void => {
          if (kol.id !== changedKolId) {
            return
          }

          onCollectedChange?.(changedKolId, isCollected)
        }}
      />
    </AddToCollectionButtonContainer>
  )
}

export const AddToCollectionButtonContainer = styled.div``

export const TriggerElement = styled.div``

export default AddToCollectionButton
